import { useLayoutEffect, useRef } from "react";

const DURATION = 240;
const EASING = "cubic-bezier(0.2, 0, 0, 1)";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * FLIP layout animation for the `[data-flip-id]` children of a container: after every render, any
 * child that moved glides from where it was to where it now is. Positions are kept relative to the
 * container, so scrolling the page never reads as movement.
 *
 * `startFrom(id, viewportRect)` overrides where one child animates from on the next render — used
 * to fly a dropped card in from the point it was let go rather than from its old slot.
 */
export const useFlipLayout = () => {
  const containerRef = useRef(null);
  const positionsRef = useRef(new Map());
  const overridesRef = useRef(new Map());
  const flightsRef = useRef(new Map());

  const relative = (rect) => {
    const box = containerRef.current.getBoundingClientRect();
    return { left: rect.left - box.left, top: rect.top - box.top };
  };

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const animate = !prefersReducedMotion();
    const next = new Map();

    container.querySelectorAll("[data-flip-id]").forEach((el) => {
      const id = el.dataset.flipId;
      // A render mid-flight (e.g. the saved layout landing) would otherwise measure the card part-way
      // through its transform and start a mirrored flight; pick up from where it visibly is instead.
      const running = flightsRef.current.get(id);
      let visible = null;
      if (running?.playState === "running") {
        visible = relative(el.getBoundingClientRect());
        running.cancel();
      }
      const now = relative(el.getBoundingClientRect());
      next.set(id, now);
      const from = overridesRef.current.get(id) ?? visible ?? positionsRef.current.get(id);
      if (!animate || !from) return;
      const dx = from.left - now.left;
      const dy = from.top - now.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      // Lift the moving card above its neighbours for the flight, then hand stacking back.
      el.style.zIndex = "20";
      const flight = el.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "translate(0, 0)" }],
        { duration: DURATION, easing: EASING },
      );
      flightsRef.current.set(id, flight);
      flight.onfinish = flight.oncancel = () => {
        if (flightsRef.current.get(id) === flight) el.style.zIndex = "";
      };
    });

    overridesRef.current.clear();
    positionsRef.current = next;
  });

  const startFrom = (id, viewportRect) => {
    if (containerRef.current && viewportRect) overridesRef.current.set(id, relative(viewportRect));
  };

  return { containerRef, startFrom };
};
